/**
 * Type definitions for the SKU payout reports feature. These mirror the
 * `PayoutLineItem` sealed-class union exposed by partners-service so that
 * siblings (list / detail / flyout / CSV export) share a single source of
 * truth for the response shape.
 *
 * Siblings reference a typedef via JSDoc:
 *   @type {import('./SkuPayoutReports.types').OrderLineItem}
 */

/**
 * @typedef {Object} PayoutLineItemBase
 * @property {string} effectiveDate ISO date (yyyy-mm-dd) the line item applies to.
 * @property {string} currencyCode ISO 4217 currency code, e.g. 'USD'.
 * @property {number} amount Pre-tax payout amount in atomic minor units (e.g. cents).
 * @property {number} amountConverted Pre-tax payout amount in major units; use for currency rendering.
 * @property {number|null} transactionAmount Customer-facing transaction amount in major units.
 * @property {number|null} taxAmount Tax portion of the transaction amount.
 * @property {number|null} totalAmount Transaction amount plus tax.
 */

/**
 * @typedef {('CP_CANCELLED'|'CP_CANCELLED_FULL_PAYOUT'|'PARTNER_CANCELLED'|'PARTNER_SOLD_OUT'|'UNBOOKABLE'|'USER_MISSED_PICKUP'|null)} CancellationType
 */

/**
 * @typedef {Object} OrderItemDetail
 * @property {string} name
 * @property {number|null} payableAmount
 * @property {number|null} basePayableAmount Per-item pre-cancellation payout, used for the strikethrough display on cancelled items.
 * @property {CancellationType} cancellationType
 */

/**
 * A single order (SKU venue post-cutover). Distinguished from a reservation
 * line item by the presence of `orderId`.
 *
 * @typedef {PayoutLineItemBase & {
 *   orderId: string,
 *   orderDateTime: string|null,
 *   items: OrderItemDetail[],
 *   userId: number|null,
 *   customerFirstName: string|null,
 *   customerLastName: string|null,
 *   customerEmail: string|null,
 *   status: string,
 *   cancellationType: CancellationType,
 *   markedFulfilledAt: string|null,
 *   markedFulfilledType: ('PARTNER'|'AUTO'|null),
 * }} OrderLineItem
 */

/**
 * A single reservation (legacy SKU / pre-cutover). Distinguished from an
 * order line item by the absence of `orderId`.
 *
 * @typedef {PayoutLineItemBase & {
 *   reservationId: number,
 *   className: string|null,
 *   firstName: string|null,
 *   lastName: string|null,
 *   startDate: string|null,
 *   startTime: string|null,
 *   status: string,
 * }} ReservationLineItem
 */

/**
 * An adjustment row. Included for completeness; the ORDER payout branch does
 * not return adjustments but siblings may need to render them on the legacy
 * branch.
 *
 * @typedef {PayoutLineItemBase & {
 *   adjustmentId: number,
 *   description: string|null,
 * }} AdjustmentLineItem
 */

/**
 * @typedef {OrderLineItem | ReservationLineItem | AdjustmentLineItem} PayoutLineItem
 */

/**
 * Discriminator: `true` when the item is an `OrderLineItem`.
 * @param {PayoutLineItem|null|undefined} item
 * @returns {boolean}
 */
export function isOrderLineItem(item) {
  return item != null && typeof item.orderId === 'string';
}

/**
 * Discriminator: `true` when the item is a `ReservationLineItem`. Items that
 * carry both `orderId` and `reservationId` on the backend are surfaced to
 * the frontend as orders (the orderId wins), so this check requires orderId
 * to be absent.
 * @param {PayoutLineItem|null|undefined} item
 * @returns {boolean}
 */
export function isReservationLineItem(item) {
  return item != null && item.orderId == null && typeof item.reservationId === 'number';
}

/**
 * Discriminator: `true` when the item is an `AdjustmentLineItem`.
 * @param {PayoutLineItem|null|undefined} item
 * @returns {boolean}
 */
export function isAdjustmentLineItem(item) {
  return item != null && typeof item.adjustmentId === 'number';
}
